// packages/frontend/src/features/recipes/descSnippet.ts
//
// Plain-text first-paragraph snippet of a recipe's markdown description, plus
// a companion predicate for whether there's anything beyond that paragraph.
// Shared by RecipeRow (list-view row snippet) and RecipeView (description
// clamp + "more" expander).

/**
 * Take the FIRST PARAGRAPH of the description as a row snippet, with markdown
 * + HTML stripped. The first paragraph is meant to be a hook; later paragraphs
 * (headings, lists, notes) don't compress well to a single line.
 *
 * The full MarkdownView in the recipe view does proper sanitized rendering.
 */
export function descSnippet(md: string | null): string | null {
  if (md === null) return null;
  // 1. Take the first paragraph — split on \n\n OR stop at a heading line.
  // We also stop at the first list-like or blockquote-like line so the preview
  // never carries structured content.
  const lines = md.split("\n");
  const firstParaLines: string[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed === "") {
      if (firstParaLines.length > 0) break; // blank line ends the first paragraph
      continue; // leading blanks
    }
    if (/^#{1,6}\s/.test(trimmed)) break; // heading
    if (/^[-*+]\s/.test(trimmed)) break; // bullet
    if (/^\d+\.\s/.test(trimmed)) break; // numbered list
    if (/^>\s/.test(trimmed)) break; // blockquote
    firstParaLines.push(trimmed);
  }
  const firstPara = firstParaLines.join(" ");
  if (firstPara.length === 0) return null;

  // 2. Strip inline markdown + HTML-tag-looking sequences. Two-pass on HTML so
  //    a malformed `<script` fragment doesn't gobble all text up to the next
  //    `<`. The full MarkdownView in the recipe view does proper sanitized
  //    rendering — this is just for the row-snippet preview.
  const stripped = firstPara
    // Drop scripty/styley/framey tags including their contents (well-formed pairs only).
    .replace(/<(script|style|iframe|object|embed)\b[\s\S]*?<\/\1\s*>/gi, "")
    // Strip remaining well-formed tags (open + close).
    .replace(/<\/?[a-z][a-z0-9]*\b[^>]*>/gi, "")
    // Strip malformed tag-start fragments — bounded by whitespace / `<` / `>`
    // so we don't accidentally eat unrelated text.
    .replace(/<\/?[a-z][a-z0-9]*[^\s<>]*/gi, "")
    .replace(/!?\[([^\]]+)\]\([^)]+\)/g, "$1") // links + images → visible text
    .replace(/[*_`]+/g, "") // inline emphasis / inline code markers
    .replace(/\s+/g, " ")
    .trim();
  return stripped.length > 0 ? stripped : null;
}

/**
 * True iff `md` has meaningful content beyond the first paragraph that
 * `descSnippet` extracts — i.e. there's something for a "more" expander to
 * reveal. Walks the same line-by-line rules as `descSnippet` (blank line /
 * heading / bullet / numbered-list / blockquote all end the first paragraph),
 * then checks whether anything non-blank follows.
 *
 * Contract note: this is only meaningful when `descSnippet(md)` is non-null
 * (i.e. there IS a first-paragraph snippet to clamp). For a description that
 * is *only* a heading/list/etc with no leading paragraph text at all,
 * `descSnippet` returns null and this function may still report `true` (the
 * heading itself counts as "more" structurally) — callers should treat that
 * combination as "nothing to clamp, render in full" rather than "show an
 * expander with an empty snippet". RecipeView's `snippet !== null` guard
 * handles this.
 */
export function hasMoreContent(md: string | null): boolean {
  if (md === null) return false;
  const lines = md.split("\n");
  let sawFirstParaLine = false;
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i]!.trim();
    if (trimmed === "") {
      if (sawFirstParaLine) {
        // Blank line ends the first paragraph — anything non-blank after it
        // (even across further blank lines) counts as "more".
        return lines.slice(i + 1).some((l) => l.trim() !== "");
      }
      continue; // leading blanks
    }
    if (/^#{1,6}\s/.test(trimmed)) return true; // heading
    if (/^[-*+]\s/.test(trimmed)) return true; // bullet
    if (/^\d+\.\s/.test(trimmed)) return true; // numbered list
    if (/^>\s/.test(trimmed)) return true; // blockquote
    sawFirstParaLine = true;
  }
  return false; // reached end without a break → the whole content IS the first paragraph
}
